import { trackProps } from '../../helpers/utils/analytics';
import { Input } from '../ui/input';
import { Box } from '../ui/box';
import { Text } from '../ui';
import { useNameLimit } from '../../helpers/utils/useNameLimit';
import { NAME_LIMIT_MESSAGE } from '../../helpers/utils/constants';

import '../../App.css';
import { HINT_PAD_Y } from './figmaLayout';

interface PlayerNameInputProps {
  teamClassName: string;
  id: string;
  /** The input's accessible name (screen readers say "Left opponent"). */
  label: string;
  /** The caps label printed above the field; defaults to `label`. */
  seatLabel?: string;
  placeholder?: string;
  /** Move an opponent's temporary hint away from the felt's centre. */
  hintOffset?: string;
  playerName: string;
  errors?: string;
  touched?: boolean;
  handleKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

// Emerald/Name field (Figma 1796:20849): 128 x 44, a team-coloured outline
// that is dashed while empty and solid once filled, 8pt side padding and one
// line that ends in an ellipsis. Team colour comes from the data-team scope.
function PlayerNameInput({
  teamClassName,
  id,
  label,
  seatLabel,
  placeholder,
  hintOffset = '0px',
  playerName,
  errors,
  touched,
  handleChange,
  onFocus,
  onBlur,
}: PlayerNameInputProps) {
  const { limit, showHint } = useNameLimit();
  const invalid = !!(errors && touched);
  const filled = playerName !== '';

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = limit(e.target.value);
    if (value !== e.target.value) e.target.value = value;
    handleChange(e);
  };

  return (
    <Box
      className={teamClassName}
      data-team={teamClassName === 'team2' ? '2' : '1'}
      display="flex"
      flexDirection="column"
      alignItems="center"
      gap="var(--spades-spacing-xs)"
      style={{ color: 'inherit' }}
    >
      <label
        htmlFor={id}
        style={{
          display: 'flex',
          alignItems: 'center',
          height: '22px',
          color: 'var(--spades-ivory)',
          fontSize: 'var(--spades-font-size-2xs)',
          fontWeight: 700,
          lineHeight: 1.25,
          letterSpacing: 'var(--spades-tracking-md)',
          whiteSpace: 'nowrap',
        }}
      >
        {seatLabel ?? label}
      </label>
      <Box position="relative">
        <Input
          {...trackProps('player_name_edit_opened')}
          unstyled
          aria-label={label}
          aria-invalid={invalid || undefined}
          placeholder={invalid ? errors : (placeholder ?? '')}
          type="text"
          value={playerName}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          id={id}
          name={id}
          data-cy={id + 'Input'}
          display="block"
          width="128px"
          height="44px"
          px="var(--spades-spacing-sm)"
          bg="var(--spades-background)"
          color="spades.ivory"
          fontSize="var(--spades-font-size-sm)"
          fontWeight="700"
          lineHeight={1.25}
          textAlign="center"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          borderWidth="1.5px"
          borderStyle={filled && !invalid ? 'solid' : 'dashed'}
          borderColor={
            invalid ? 'var(--spades-error)' : 'var(--spades-team-base)'
          }
          borderRadius="var(--spades-radius-control)"
          caretColor="var(--spades-team-base)"
          _placeholder={{
            color: invalid ? 'var(--spades-error)' : 'var(--spades-muted)',
            opacity: invalid ? 1 : 0.6,
            fontWeight: 400,
          }}
          _focus={{
            outline: 'none',
            // The caret blinks where the text starts, the field's centre, so
            // a placeholder there would be split by it. The seat label above
            // still says whose name this is.
            _placeholder: { opacity: 0 },
            borderWidth: '2px',
            borderStyle: 'solid',
            borderColor: 'var(--spades-team-base)',
            boxShadow:
              '0 0 7px color-mix(in srgb, var(--spades-team-base) 50%, transparent)',
          }}
        />
        {/* The brief limit hint takes the seat's own label row, right above
            the name being typed. The seat already keeps that row clear of the
            felt print, the dealer hint and the other seats at every table
            height, so the hint can't run into them (2026-10-18: under the
            partner's field it covered GAME IN PROGRESS). Nothing moves. */}
        {showHint && (
          <Text
            role="status"
            color="spades.error"
            fontSize="var(--spades-font-size-2xs)"
            fontWeight="700"
            position="absolute"
            // Centres the 21pt pill on the 22pt label row above the field.
            bottom="calc(100% + var(--spades-spacing-xs) + 0.5px)"
            left={`calc(50% + ${hintOffset})`}
            transform="translateX(-50%)"
            zIndex={1}
            bg="var(--spades-background)"
            borderRadius="var(--spades-radius-pill)"
            px="var(--spades-spacing-sm)"
            py={HINT_PAD_Y}
            lineHeight={1.25}
            whiteSpace="nowrap"
            pointerEvents="none"
          >
            {NAME_LIMIT_MESSAGE}
          </Text>
        )}
      </Box>
    </Box>
  );
}

export default PlayerNameInput;
